/**
 * One session read, cached in one place, read by both halves of the app.
 *
 * A router guard runs outside React and a component runs inside it, and they must
 * never disagree about who is signed in — a guard that lets a page through while
 * the shell renders "signed out" is the bug this file exists to make impossible.
 * So the session is a TanStack Query entry like everything else: `beforeLoad` calls
 * `ensureQueryData`, the provider calls `useQuery`, and both get the same object
 * from the same cache.
 *
 * That also makes invalidation ordinary. Signing out, switching organisation and
 * stopping an impersonation each change who this is; each invalidates
 * `queryKeys.session.*` and every subscriber follows.
 *
 * Better Auth's own React hooks (`authClient.useSession()`) are deliberately not
 * used. They are a second cache with a second invalidation story, and the guards
 * cannot read a nanostore before the tree renders anyway.
 */

import type { QueryClient } from '@tanstack/react-query'

import { studioRoleOf } from '@fareast/shared'

import { queryKeys } from '../api/keys.js'
import { ApiProblem, NetworkError } from '../api/problem.js'
import type { AuthError, AuthResponse, PortalAuthClient } from './client.js'
import {
  activeClientId,
  NO_SESSION,
  principalOf,
  type ActiveMemberShape,
  type Organization,
  type SessionShape,
  type SessionState,
} from './principal.js'

/**
 * Thirty seconds.
 *
 * Long enough that a click-through of four pages is one request; short enough that
 * an expiry, a role change or an impersonation started in another tab is noticed
 * inside a page view. Every event that *we* cause invalidates explicitly, so this
 * only covers the ones we do not see.
 */
export const SESSION_STALE_MS = 30_000

/**
 * **The one predicate: did the session endpoint say this person is not signed in?**
 *
 * `.docs/00-index.md` §2.6 — it was written twice, once here and once as "whatever
 * `getSession()` did not return data for", and the second copy is R1-02: any 5xx
 * from `GET /api/auth/get-session` hard-redirected a signed-in person to `/login`
 * while their session row was live and unexpired, and the magic-link throttle then
 * kept them out. Two of those happened by themselves when the dev API blipped.
 *
 * The whole table, because "not 200" is not the question:
 *
 * | answer | means | what happens |
 * |---|---|---|
 * | `200` with a session | signed in | the principal |
 * | `200` with `null` — Better Auth's own "nobody" | signed out | `/login` |
 * | `401` — the cookie is not accepted | signed out | `/login` |
 * | `403` — the account is refused (the `admin` plugin bans this way) | signed out | `/login` |
 * | `404`, `408`, `429` | the read failed | the boundary, session kept |
 * | any `5xx`, a gateway's HTML, a body that is not JSON | the read failed | the boundary, session kept |
 * | a request that never got an answer | the read failed | the boundary, session kept |
 *
 * An answer carrying **no** status at all is a read that failed too. The direction
 * of the guess is the point: signing somebody out is destructive and irreversible
 * inside the resend throttle, and showing them "try again" is not.
 *
 * This is deliberately **not** `api/problem.ts` `isUnauthenticated`, which asks a
 * different question of a different call — "did *this page's* request say the
 * session ended", where `403` means "you are signed in and may not read that" and
 * must never sign anybody out. Same number, opposite answer; one predicate over
 * both would be the bug rather than the fix.
 */
export function meansSignedOut(status: number | null | undefined): boolean {
  return status === 401 || status === 403
}

export function sessionQueryOptions(auth: PortalAuthClient) {
  return {
    queryKey: queryKeys.session.principal(),
    queryFn: () => readSession(auth),
    staleTime: SESSION_STALE_MS,
    /**
     * One attempt, and the failure is the answer.
     *
     * A read that says "signed out" resolves to `NO_SESSION` and the guards land on
     * `/login`; a read that *failed* rejects, and the route boundary says so while
     * the cookie stays exactly where it was. Retrying here would only delay both.
     */
    retry: false,
  }
}

/** Everything that changes when the answer to "who is this" changes. */
export async function invalidateSession(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: queryKeys.session.principal() })
}

/**
 * The three reads behind a principal.
 *
 * The membership calls only happen for a client user: staff hold no seat, and
 * asking would be two requests per navigation that always answer nothing.
 *
 * The role is asked for **by organisation id** rather than by "whichever is
 * active", through `/organization/get-active-member-role`. Its neighbour
 * `/organization/get-active-member` reads `session.activeOrganizationId` and
 * nothing else, and that column is null for a perfectly ordinary person who has
 * signed in once and belongs to one company, because Better Auth does not set it
 * at sign-in — so it answers `NO_ACTIVE_ORGANIZATION` for the commonest case there
 * is. The API resolves that case itself (`resolvers.ts`, rule 2: "belonging to one
 * company is not a choice"), `activeClientId` applies the same ladder here, and
 * the two agree without the SPA writing to the session row to make them.
 */
export async function readSession(auth: PortalAuthClient): Promise<SessionState> {
  const session = await getSession(auth)
  if (session === null) return NO_SESSION

  // Both tiers short-circuit the two membership reads (session 35): a restricted
  // studio account holds no seat either, so asking would be two requests per
  // navigation that always answer nothing. `studioRoleOf` is the one classifier,
  // shared with `principalOf` above and with the API's resolver.
  if (studioRoleOf(session.user.role) !== null) {
    return { principal: principalOf(session, null, []), organizations: [] }
  }

  const organizations = await listOrganizations(auth)
  const clientId = activeClientId(session, organizations)
  const activeMember = clientId === null ? null : await getActiveMemberRole(auth, clientId)

  return { principal: principalOf(session, activeMember, organizations), organizations }
}

/* ------------------------------------------------------------------- internals */

/**
 * The session read, and the only one of the three that may fail loudly.
 *
 * `meansSignedOut` decides; everything else it could have been — a gateway, a
 * timeout, a rate limit, a body that is not JSON — becomes an error the boundary
 * renders, with the cookie untouched.
 */
async function getSession(auth: PortalAuthClient): Promise<SessionShape | null> {
  const answer = await ask(() => auth.getSession())
  const { data, error } = answer
  if (error != null && !meansSignedOut(error.status)) throw unreadableSession(error)
  return data ?? null
}

/**
 * The other two are `{ data, error }` rather than a throw, and each treats an error
 * as "nothing". Somebody who holds no seat at all gets an error from the role read,
 * which is a fact about their account and not a failure — `tenantDbOf` on the API
 * says the same thing with `NoActiveOrganization`, and the shell's switcher is what
 * resolves the case where there is a choice to make.
 *
 * A *rejection* is still a rejection: `ask` turns it into a `NetworkError` rather
 * than letting a raw `TypeError` reach the boundary as "something went wrong",
 * because a portal that cannot be reached has a sentence of its own.
 */
async function listOrganizations(auth: PortalAuthClient): Promise<readonly Organization[]> {
  const { data } = await ask(() => auth.organization.list())
  return (data ?? []).map((organization) => ({
    id: organization.id,
    name: organization.name,
    slug: organization.slug,
  }))
}

async function getActiveMemberRole(
  auth: PortalAuthClient,
  organizationId: string,
): Promise<ActiveMemberShape> {
  const { data } = await ask(() =>
    auth.organization.getActiveMemberRole({ query: { organizationId } }),
  )
  return data ?? null
}

/** A Better Auth call, with the one case it does not answer turned into the app's own type. */
async function ask<T>(call: () => Promise<AuthResponse<T>>): Promise<AuthResponse<T>> {
  try {
    return await call()
  } catch (cause) {
    throw new NetworkError(cause)
  }
}

/**
 * A session read that got an answer nobody can act on.
 *
 * It is an `ApiProblem` rather than a fourth error type because the boundary
 * already knows two, and this is exactly the one `problem.ts` describes as "a
 * response that is neither — nginx's own 502 page, a gateway that answers HTML":
 * `fromBody: false`, code `Internal`, and a sentence written for the person in
 * front of it. The sentence says the thing that is actually at stake, which is
 * that nothing has signed them out.
 */
function unreadableSession(error: AuthError): ApiProblem {
  return new ApiProblem({
    title: 'Something went wrong',
    status: error.status ?? 500,
    code: 'Internal',
    detail: 'We could not check your sign-in. You have not been signed out — try again.',
    fromBody: false,
  })
}
